import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Send, Sparkles, Paperclip, AlertTriangle, Wand2, Info, Eye } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Guests } from '../lib/api';
import EmailPreviewDialog from './EmailPreviewDialog';

const formatRecTime = (guest) => {
  const v = guest?.recordingDate || guest?.episodeDate || '';
  if (!v) return '';
  try {
    const d = new Date(v.includes('T') ? v : `${v}T00:00`);
    return v.includes('T')
      ? d.toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })
      : d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  } catch { return v; }
};

const applyTemplate = (text, guest, show) => {
  if (!text) return '';
  const firstName = (guest?.name || '').split(' ')[0] || 'there';
  const releaseLink = `${window.location.origin}/release/${guest?.id || ''}`;
  return text
    .replace(/{{firstName}}/g, firstName)
    .replace(/{{episode}}/g, guest?.episodeTitle || 'your episode')
    .replace(/{{episodeDate}}/g, guest?.episodeDate || '')
    .replace(/{{recordingTime}}/g, formatRecTime(guest))
    .replace(/{{showName}}/g, show?.name || 'the show')
    .replace(/{{releaseLink}}/g, releaseLink)
    .replace(/{{appleLink}}/g, guest?.platforms?.apple || '')
    .replace(/{{websiteLink}}/g, guest?.platforms?.libsyn || '')
    .replace(/{{spotifyLink}}/g, guest?.platforms?.spotify || '')
    .replace(/{{youtubeLink}}/g, guest?.platforms?.youtube || '')
    .replace(/{{riversideLink}}/g, guest?.riversideLink || '')
    .replace(/{{location}}/g, guest?.location || '')
    .replace(/{{bookingLink}}/g, `${window.location.origin}/book/${show?.id || ''}`)
    .replace(/{{company}}/g, guest?.company || '')
    .replace(/{{showFocus}}/g, show?.id === 'wine-talks' ? 'wine' : 'American music & culture')
    .replace(/{{supportLink}}/g, `${window.location.origin}/support`)
    .replace(/{{listenersSupportLink}}/g, `${window.location.origin}/support/listeners`)
    .replace(/{{guestsSupportLink}}/g, `${window.location.origin}/support/guests`);
};

export default function EmailComposer({ open, onOpenChange, initialGuestId, initialTemplateId }) {
  const { guests, templates, activeShow, filteredGuests, refreshGuests } = useApp();
  const [guestId, setGuestId] = useState(initialGuestId || '');
  const [templateId, setTemplateId] = useState(initialTemplateId || '');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [cc, setCc] = useState('');
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [attachThumbs, setAttachThumbs] = useState(false);

  // Auto-enable thumbnail attachments for the "Show Assets Delivery" template
  useEffect(() => {
    setAttachThumbs(templateId === 't4');
  }, [templateId]);

  const generateAi = async (intent) => {
    if (!guestId) { toast({ title: 'Pick a guest first' }); return; }
    setDrafting(true);
    try {
      const r = await Guests.aiDraft(guestId, intent);
      setSubject(r.subject || subject);
      setBody(r.body || body);
      toast({ title: 'Draft ready', description: 'Edit before sending if you like.' });
    } catch (e) {
      toast({ title: 'AI draft failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setDrafting(false);
    }
  };

  useEffect(() => {
    if (open) {
      setGuestId(initialGuestId || '');
      setTemplateId(initialTemplateId || '');
    }
  }, [open, initialGuestId, initialTemplateId]);

  useEffect(() => {
    const g = guests.find(x => x.id === guestId);
    if (templateId === '__blank') {
      setSubject('');
      setBody('');
    } else {
      const t = templates.find(x => x.id === templateId);
      if (t) {
        setSubject(applyTemplate(t.subject, g, activeShow));
        setBody(applyTemplate(t.body, g, activeShow));
      }
    }
    if (g) setCc((g.interestedParties || []).join(', '));
  }, [guestId, templateId, guests, templates, activeShow]);

  const guest = guests.find(g => g.id === guestId);

  const handleSend = async () => {
    if (!guest) { toast({ title: 'Select a guest' }); return; }
    setSending(true);
    try {
      const ccArr = cc.split(',').map(x => x.trim()).filter(Boolean);
      const res = await Guests.sendEmail(guest.id, { templateId, subject, body, cc: ccArr, attachThumbnails: attachThumbs });
      if (res.sent) {
        const att = res.attachments?.length ? ` with ${res.attachments.length} image${res.attachments.length === 1 ? '' : 's'} attached` : '';
        toast({ title: 'Email sent', description: `Delivered to ${guest.email}${att}` });
      } else if (res.reason === 'microsoft_not_connected') {
        toast({ title: 'Microsoft not connected', description: 'Connect your Outlook in Settings to send real emails.' });
      } else if (res.reason === 'no_email') {
        toast({ title: 'No email address', description: res.message, duration: 8000 });
        return;
      } else {
        toast({ title: 'Send failed', description: res.message || 'Outlook rejected the message. Try again in a moment.', duration: 10000 });
        return;
      }
      await refreshGuests();
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Send failed', description: String(e?.message || e) });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between" style={{ background: `${activeShow.color}0f` }}>
          <DialogHeader>
            <DialogTitle className="text-[15px]">Compose email</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-1.5 text-[11px] text-stone-500">
            <Sparkles className="w-3.5 h-3.5" style={{ color: activeShow.color }} /> Personalized via merge tags
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="merge-fields-info-btn" type="button" className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-stone-200 hover:bg-stone-50 text-[10.5px] text-stone-600">
                  <Info className="w-3 h-3" /> View fields
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[360px] p-0">
                <div className="px-3 py-2 border-b border-stone-200 text-[12px] font-medium text-stone-800">Merge fields for this email</div>
                <div className="max-h-[300px] overflow-y-auto divide-y divide-stone-100">
                  {(() => {
                    const g = guests.find(x => x.id === guestId);
                    const fn = (g?.name || '').split(' ')[0] || '—';
                    const fields = [
                      { tag: '{{firstName}}', src: "Guest's name (first word)", val: fn },
                      { tag: '{{showName}}', src: 'Active show', val: activeShow?.name || '—' },
                      { tag: '{{episode}}', src: "Guest's episode title", val: g?.episodeTitle || '— (empty)' },
                      { tag: '{{episodeDate}}', src: "Guest's air date", val: g?.episodeDate || '— (empty)' },
                      { tag: '{{recordingTime}}', src: "Guest's recording date & time", val: formatRecTime(g) || '— (empty)' },
                      { tag: '{{location}}', src: "Guest's location field", val: g?.location || '— (empty)' },
                      { tag: '{{riversideLink}}', src: 'Guest → Riverside link', val: g?.riversideLink || '— (empty)' },
                      { tag: '{{supportLink}}', src: 'Public support page (combined)', val: `${window.location.origin}/support` },
                      { tag: '{{listenersSupportLink}}', src: 'Listeners support page', val: `${window.location.origin}/support/listeners` },
                      { tag: '{{guestsSupportLink}}', src: 'Guests & PR support page', val: `${window.location.origin}/support/guests` },
                      { tag: '{{releaseLink}}', src: 'Public release form URL', val: g ? `${window.location.origin}/release/${g.id}` : 'pick a guest' },
                      { tag: '{{appleLink}}', src: 'Guest → platforms.apple', val: g?.platforms?.apple || '— (empty)' },
                      { tag: '{{websiteLink}}', src: 'Guest → platforms (show website page)', val: g?.platforms?.libsyn || '— (empty)' },
                      { tag: '{{spotifyLink}}', src: 'Guest → platforms.spotify', val: g?.platforms?.spotify || '— (empty)' },
                      { tag: '{{youtubeLink}}', src: 'Guest → platforms.youtube', val: g?.platforms?.youtube || '— (empty)' }
                    ];
                    return fields.map(f => (
                      <div key={f.tag} className="px-3 py-2 text-[11.5px]">
                        <div className="flex items-center justify-between gap-2">
                          <code className="bg-stone-100 px-1.5 py-0.5 rounded text-stone-800">{f.tag}</code>
                          <span className="text-stone-400 text-[10px]">{f.src}</span>
                        </div>
                        <div className="mt-1 text-stone-700 truncate" title={f.val}>→ {f.val}</div>
                      </div>
                    ));
                  })()}
                  <div className="px-3 py-2 bg-amber-50 text-[11px] text-amber-800">
                    <div className="font-medium mb-0.5">Prospect-only fields (not used here)</div>
                    <div><code>{'{{referrer}}'}</code> {'→'} Prospect {'\u2192'} Source detail (fallback: a mutual contact)</div>
                    <div><code>{'{{topic}}'}</code> {'→'} Prospect {'\u2192'} Source detail (fallback: your work)</div>
                    <div><code>{'{{bookingLink}}'}</code> {'→'} Show {'\u2192'} public /book/ URL</div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase text-stone-500">To (guest)</Label>
              <Select value={guestId} onValueChange={setGuestId}>
                <SelectTrigger><SelectValue placeholder="Select guest" /></SelectTrigger>
                <SelectContent>
                  {filteredGuests.map(g => <SelectItem key={g.id} value={g.id}>{g.name} — {g.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-stone-500">Template</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger><SelectValue placeholder="Choose template" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__blank" data-testid="composer-blank-letter-option">✍️ Blank letter — write from scratch</SelectItem>
                  {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase text-stone-500">Cc (interested parties)</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="producer@..., editor@..." />
          </div>

          <div>
            <Label className="text-[11px] uppercase text-stone-500">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>

          <div>
            <Label className="text-[11px] uppercase text-stone-500">Body</Label>
            <Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} className="font-mono text-[12.5px]" />
          </div>

          {guest && (guest.thumbnail || guest.thumbnail16x9) && (
            <label data-testid="attach-thumbnails-toggle" className="p-2.5 rounded-md border border-stone-200 bg-stone-50 flex items-center gap-2.5 cursor-pointer hover:bg-stone-100">
              <input
                type="checkbox"
                checked={attachThumbs}
                onChange={(e) => setAttachThumbs(e.target.checked)}
                className="w-4 h-4 accent-stone-900"
              />
              <Paperclip className="w-3.5 h-3.5 text-stone-500" />
              <span className="text-[12px] text-stone-700">
                Attach episode thumbnails
                <span className="text-stone-500"> — {[guest.thumbnail && 'square', guest.thumbnail16x9 && '16:9 (YouTube)'].filter(Boolean).join(' + ')}</span>
              </span>
              <span className="ml-auto flex items-center gap-1.5">
                {guest.thumbnail && <img src={guest.thumbnail} alt="square" className="w-7 h-7 rounded object-cover border border-stone-200" />}
                {guest.thumbnail16x9 && <img src={guest.thumbnail16x9} alt="16:9" className="w-12 h-7 rounded object-cover border border-stone-200" />}
              </span>
            </label>
          )}
          {guest && templateId === 't4' && !guest.thumbnail && !guest.thumbnail16x9 && (
            <div data-testid="no-thumbnails-warning" className="p-2.5 rounded-md border border-amber-200 bg-amber-50 flex items-center gap-2 text-[12px] text-amber-900">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              This template says thumbnails are attached, but {guest.name.split(' ')[0]} has no thumbnails uploaded yet (guest → Assets tab).
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center gap-2">
              <Select onValueChange={(v) => generateAi(v)} disabled={drafting || !guestId}>
                <SelectTrigger className="w-44 h-9 text-[12.5px]">
                  <SelectValue placeholder={drafting ? 'Generating…' : 'Generate with AI'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="intro">Initial outreach</SelectItem>
                  <SelectItem value="followup">Follow-up nudge</SelectItem>
                  <SelectItem value="thanks">Thank you</SelectItem>
                  <SelectItem value="assets">Episode live / assets</SelectItem>
                  <SelectItem value="custom">Contextual draft</SelectItem>
                </SelectContent>
              </Select>
              <Wand2 className="w-3.5 h-3.5 text-stone-400" />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" data-testid="composer-preview-btn" onClick={() => setPreviewOpen(true)} disabled={!guest}>
                <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleSend} disabled={sending} className="text-white" style={{ background: activeShow.color }}>
                <Send className="w-3.5 h-3.5 mr-1.5" /> {sending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
        <EmailPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} subject={subject} body={body} guestId={guest?.id} toEmail={guest?.email} />
      </DialogContent>
    </Dialog>
  );
}
